import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Popover,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Button,
  Tooltip,
  Stack,
  Divider,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkOutlineIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { quickLinksApi, QuickLink, QUICK_LINKS_MAX } from 'shared/services/quickLinksApi';

interface Props {
  /** When true, the sidebar is collapsed → render as icon only. */
  collapsed?: boolean;
}

/**
 * Sidebar entry that opens a popover with the user's saved Quick Links
 * (favoritos). Per-user, persisted to the BE, max 7 entries. The popover lets
 * the user navigate to a saved link, add the current page, edit any entry's
 * label/path inline, or delete an entry.
 *
 * The button itself is rendered to look like the rest of the sidebar items so
 * it slots in cleanly at the desired position.
 */
export const QuickLinksSidebarItem: React.FC<Props> = ({ collapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const anchorRef = useRef<HTMLDivElement | null>(null);

  const [open, setOpen] = useState(false);
  const [links, setLinks] = useState<QuickLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editPath, setEditPath] = useState('');
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' | 'info' } | null>(null);

  // Lazy-load the list on first open. We don't preload because it'd be one
  // extra request on every page render for a feature most users won't open.
  useEffect(() => {
    if (!open || links.length > 0) return;
    let cancelled = false;
    setLoading(true);
    quickLinksApi
      .get()
      .then((rows) => { if (!cancelled) setLinks(rows); })
      .catch((err) => {
        if (!cancelled) setToast({ msg: err?.message || 'No se pudieron cargar los enlaces.', severity: 'error' });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const persist = async (next: QuickLink[]) => {
    setSaving(true);
    try {
      const saved = await quickLinksApi.update(next);
      setLinks(saved);
    } catch (err: any) {
      setToast({ msg: err?.message || 'No se pudo guardar.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddCurrent = () => {
    if (links.length >= QUICK_LINKS_MAX) return;
    const path = location.pathname + (location.search || '');
    if (links.some((l) => l.path === path)) {
      setToast({ msg: 'La página actual ya está en sus enlaces.', severity: 'info' });
      return;
    }
    const defaultLabel = (document.title || 'Nuevo enlace').replace(/\s*[-|·]\s*Safety.*$/i, '').slice(0, 60) || 'Nuevo enlace';
    const next = [...links, { label: defaultLabel, path }];
    void persist(next);
  };

  const handleNavigate = (l: QuickLink) => {
    navigate(l.path);
    setOpen(false);
  };

  const startEdit = (idx: number) => {
    setEditingIndex(idx);
    setEditLabel(links[idx].label);
    setEditPath(links[idx].path);
  };
  const cancelEdit = () => {
    setEditingIndex(null);
    setEditLabel('');
    setEditPath('');
  };
  const saveEdit = () => {
    if (editingIndex == null) return;
    const label = editLabel.trim().slice(0, 60);
    const path = editPath.trim().slice(0, 500);
    if (!label || !path || !path.startsWith('/')) {
      setToast({ msg: 'La ruta debe empezar con / y el nombre no puede estar vacío.', severity: 'error' });
      return;
    }
    const next = links.map((l, i) => (i === editingIndex ? { label, path } : l));
    cancelEdit();
    void persist(next);
  };
  const removeAt = (idx: number) => {
    const next = links.filter((_, i) => i !== idx);
    void persist(next);
  };

  const atCapacity = links.length >= QUICK_LINKS_MAX;

  return (
    <>
      {/* Sidebar trigger — replicates the layout of SidebarMenuItem so the
          paddings, icon-bubble, label sizing, and hover/selected behavior all
          match the rest of the rail. Outer wrapper provides the same 12-px
          left inset; the inner pressable element gets the rounded corners and
          minimum 52-px row height. */}
      <Box
        sx={{
          pl: collapsed ? 0.5 : 1.5,
          pr: 0,
          mb: 0.3,
          position: 'relative',
        }}
      >
        <Box
          ref={anchorRef}
          role="button"
          tabIndex={0}
          onClick={() => setOpen((o) => !o)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen((o) => !o);
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            pl: collapsed ? 1.25 : 2,
            pr: collapsed ? 1.25 : 1.5,
            py: 1.6,
            minHeight: 52,
            minWidth: collapsed ? 48 : 'auto',
            justifyContent: collapsed ? 'center' : 'flex-start',
            borderRadius: '14px 0 0 14px',
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
            '&:hover': {
              // Mirror the selected/active sidebar item background AND lift
              // the icon badge + label to full contrast — same fix applied
              // to SidebarMenuItem so the rail behaves consistently. The
              // label needs its `color` swapped too, not just opacity —
              // sidebartext is the light color meant for the dark rail
              // background; against this light hover plate it reads as
              // near-invisible unless it also switches to
              // sidebarSelectedText (same token the icon badge already
              // uses below).
              backgroundColor: (theme) => theme.palette.background.default,
              '& .ql-label': {
                opacity: 1,
                color: (theme) => theme.palette.background.sidebarSelectedText,
              },
              '& .ql-icon-badge': {
                backgroundColor: (theme) =>
                  `${theme.palette.background.sidebarSelectedText}28`,
                color: (theme) => theme.palette.background.sidebarSelectedText,
              },
            },
          }}
        >
          {/* Icon bubble — same dimensions as SidebarMenuItem's icon */}
          <Box sx={{
            minWidth: collapsed ? 0 : 42,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Box className="ql-icon-badge" sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (theme) => `${theme.palette.background.sidebartext}15`,
              color: (theme) => theme.palette.background.sidebartext,
              transition: 'background-color 0.15s ease, color 0.15s ease',
              '& svg': { fontSize: 20 },
            }}>
              <BookmarkIcon />
            </Box>
          </Box>
          {!collapsed && (
            <Box
              className="ql-label"
              sx={{
                color: (theme) => theme.palette.background.sidebartext,
                opacity: 0.7,
                fontWeight: 500,
                fontSize: '0.85rem',
                transition: 'color 0.3s ease, opacity 0.3s ease',
              }}
            >
              Favoritos
            </Box>
          )}
        </Box>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => { cancelEdit(); setOpen(false); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 320, maxWidth: '90vw' } } }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Favoritos
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {links.length} / {QUICK_LINKS_MAX}
          </Typography>
        </Box>
        <Divider />

        {loading && (
          <Box sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Cargando…</Typography>
          </Box>
        )}

        {!loading && links.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Stack spacing={1} alignItems="center" sx={{ textAlign: 'center', py: 1 }}>
              <BookmarkOutlineIcon sx={{ fontSize: 32, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary">
                Aún no tiene enlaces guardados.
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Use el botón de abajo para agregar la página actual.
              </Typography>
            </Stack>
          </Box>
        )}

        {!loading && links.length > 0 && (
          <List dense disablePadding>
            {links.map((l, idx) => {
              const isEditing = editingIndex === idx;
              return (
                <Box key={`${l.path}-${idx}`} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                  {!isEditing ? (
                    <ListItemButton onClick={() => handleNavigate(l)} sx={{ pr: 8 }}>
                      <ListItemText
                        primary={l.label}
                        secondary={l.path}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 600, noWrap: true }}
                        secondaryTypographyProps={{ variant: 'caption', noWrap: true, sx: { color: 'text.disabled' } }}
                      />
                      <Box sx={{ position: 'absolute', right: 4, display: 'flex' }}>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); startEdit(idx); }}
                          >
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); removeAt(idx); }}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItemButton>
                  ) : (
                    <Box sx={{ px: 2, py: 1 }}>
                      <Stack spacing={1}>
                        <TextField
                          size="small"
                          label="Nombre"
                          value={editLabel}
                          onChange={(e) => setEditLabel(e.target.value.slice(0, 60))}
                          fullWidth
                        />
                        <TextField
                          size="small"
                          label="Ruta"
                          value={editPath}
                          onChange={(e) => setEditPath(e.target.value.slice(0, 500))}
                          fullWidth
                          helperText="Debe empezar con /"
                        />
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" onClick={cancelEdit} startIcon={<CloseIcon sx={{ fontSize: 14 }} />}>
                            Cancelar
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={saveEdit}
                            disabled={saving}
                            startIcon={<SaveIcon sx={{ fontSize: 14 }} />}
                          >
                            Guardar
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  )}
                </Box>
              );
            })}
          </List>
        )}

        <Divider />
        <Box sx={{ p: 1.5 }}>
          <Tooltip title={atCapacity ? `Máximo ${QUICK_LINKS_MAX} enlaces` : ''}>
            <span>
              <Button
                fullWidth
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={atCapacity || saving}
                onClick={handleAddCurrent}
              >
                Agregar página actual
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Popover>

      <Snackbar
        open={!!toast}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            onClose={() => setToast(null)}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export default QuickLinksSidebarItem;
